import { FC, Fragment, useEffect, useMemo, useState } from "react";
// i18next
import { useTranslation } from "react-i18next";
// Hooks
import useToggle from "app/hooks/useToggle";
import useAccountStatus from "app/hooks/useAccountStatus";
// Redux
import { useAppDispatch, useAppSelector } from "app/hooks/useStore";
// Async
import { patchCalendarEvent } from "app/store/CalendarEvents/CalendarEventsAsync";
// Selectors
import { selectCurrentAccount } from "app/store/Accounts/Accounts.selectors";
import { selectCurrentDate, selectCurrentView, selectFullCalendarEvents } from "app/store/CalendarEvents/CalendarEventsSelectors";
// Components
import EventsFormDialog from "./EventsFormDialog";
import EventPopover from "./EventPopover";
import DayEventsPopover from "./DayEventsPopover";
//
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction'
import multiMonthPlugin from '@fullcalendar/multimonth';
import { DatesSetArg, EventClickArg } from "@fullcalendar/core";
// dayjs
import dayjs from "dayjs";
import isBetween from 'dayjs/plugin/isBetween';
dayjs.extend(isBetween);

type Props = {
  calendarRef: React.RefObject<any>;
  onDatesSet: (arg: DatesSetArg) => void;
}

const format = 'YYYY-MM-DDTHH:mm:ss.SSS';

const getMaxColumns = (width: number): number => {
  if (width >= 1900) return 6;
  if (width >= 1240) return 4;
  if (width >= 1000) return 3;
  if (width >= 720) return 2;
  return 1;
};
 
const CalendarBody:FC<Props> = ({ calendarRef, onDatesSet }) => {
  const { t } = useTranslation();
  
  const dispatch = useAppDispatch();

  const { isSuspended } = useAccountStatus();

  const currentAccount = useAppSelector(selectCurrentAccount);
  const events = useAppSelector(selectFullCalendarEvents) || [];
  const currentView = useAppSelector(selectCurrentView);
  const currentDate = useAppSelector(selectCurrentDate);

  const { open, toggle } = useToggle();

  const [selectInfo, setSelectInfo] = useState<any>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const [eventsAnchorEl, setEventsAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [eventsIdsForDate, setEventsIdsForDate] = useState<number[]>([]);


  const [multiMonthYearMaxColumns, setMultiMonthYearMaxColumns] = useState<number>(getMaxColumns(window.innerWidth));

  const handlerScreenWidth = () => {
    setMultiMonthYearMaxColumns(getMaxColumns(window.innerWidth));
  }

  useEffect(() => {
    window.addEventListener('resize', handlerScreenWidth);

    return () => {
      window.removeEventListener('resize', handlerScreenWidth);
    }
  }, []);

  const handleEventClick = (clickInfo: EventClickArg) => {
    clickInfo.jsEvent.stopPropagation();
    setAnchorEl(clickInfo.el);
    setSelectedEventId(clickInfo.event.id);
  }

  const onClosePopover = () => {
    setAnchorEl(null);
    setSelectedEventId(null);
  }

  const onCloseEventsPopover = () => {
    setEventsAnchorEl(null);
    setEventsIdsForDate([]);
  }

  const handleSelect = (selectInfo: any) => {
    const { end, allDay } = selectInfo;

    const eventData = {
      ...selectInfo,
      end: allDay ? dayjs(end).subtract(1, 'day') : end,
      endStr: dayjs(end).subtract(allDay ? 1 : 0, 'day').format('YYYY-MM-DD')
    }
    
    setSelectInfo(eventData);
    toggle();
  }

  const handleEdit = (editInfo: any) => {
    const { id, start, end, allDay } = editInfo.event;

    const editData = {
      eventId: Number(id),
      data: {
        allDay: allDay,
        start: `${dayjs.utc(start).format(format)}Z`,
        end: end ? `${dayjs.utc(end).format(format)}Z` : allDay
          ? `${dayjs.utc(start).add(1, 'day').format(format)}Z`
          : `${dayjs.utc(start).add(1, 'hour').format(format)}Z`
      }
    }

    dispatch(patchCalendarEvent(editData));
  }

  const handleDateClick = (arg: DateClickArg) => {
    const calendarApi = calendarRef.current?.getApi();
    const viewType = calendarApi?.view?.type;
  
    if (viewType?.startsWith('multiMonth')) {
      const clickedDate = dayjs(arg.dateStr).startOf('day');
  
      const matchedEvents = calendarApi?.getEvents().filter((event: any) => {
        const clicked = dayjs(arg.dateStr).startOf('day');
        const start = dayjs(event.start).startOf('day');
        const endRaw = event.end || event.start;
        const end = dayjs(endRaw).startOf('day');
      
        if (event.allDay) {
          return clicked.isSame(start, 'day') || clicked.isBetween(start, end, null, '[)');
        } else {
          return clicked.isSame(start, 'day') || (clicked.isAfter(start) && clicked.isBefore(end));
        }
      }).map((e:any) => Number(e.id));

      setSelectedDate(clickedDate.toDate());
      setEventsIdsForDate(matchedEvents || []);
      setEventsAnchorEl(arg.jsEvent.target as HTMLElement);
    }
  };

  const openDayView = (dateStr: string) => {
    const calendarApi = calendarRef.current?.getApi();
    calendarApi?.changeView('timeGridDay', dateStr);
    onCloseEventsPopover();
  }

  const isMultiMonthView = useMemo(() => currentView.startsWith('multiMonth'), [currentView]);
  const timeFormat = currentAccount?.settings?.timeFormat || 'hh:mm a';
  
  return (
    <Fragment>
      <FullCalendar
        timeZone="UTC"
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, multiMonthPlugin]}
        initialView={currentView}
        initialDate={currentDate.toDate()}
        views={{
          multiMonthThree: {
            type: 'multiMonth',
            duration: { months: 3 },
          },
          multiMonthYear: {
            type: 'multiMonth',
            duration: { months: 12 },
          },
        }}
        headerToolbar={false}
        buttonText={{
          today: t('labels.today'),
          month: t('labels.month'),
          week: t('labels.week'),
          day: t('labels.day'),
          multiMonthThree: t('labels.quarter'),
          multiMonthYear: t('labels.year')
        }}
        editable={!isSuspended}
        eventDrop={handleEdit}
        eventResize={handleEdit}
        selectable={!isMultiMonthView && !isSuspended}
        selectMirror={!isMultiMonthView && !isSuspended}
        dayMaxEvents
        events={events}
        select={!isMultiMonthView && !isSuspended ? handleSelect : undefined}
        eventClick={!isMultiMonthView ? handleEventClick : undefined}
        height="100%"
        datesSet={onDatesSet}
        eventTimeFormat={{
          hour: 'numeric',
          minute: '2-digit',
          hour12: timeFormat === 'hh:mm a',
          meridiem: 'short',
          omitZeroMinute: true
        }}
        eventDidMount={(info) => {
          info.el.style.cursor = "pointer";
        }}
        dateClick={handleDateClick}
        multiMonthMaxColumns={currentView === 'multiMonthYear' ? multiMonthYearMaxColumns : 3}
        multiMonthMinWidth={currentView === 'multiMonthYear' ? 100 : 300}
        multiMonthTitleFormat={{
          month: 'long',
          week: 'numeric',
        }}
        showNonCurrentDates
        moreLinkClick={(arg:any) => {
          const eventIds = arg.allSegs.map((seg:any) => Number(seg.event.id));
          setSelectedDate(dayjs(arg.date).toDate());
          setEventsIdsForDate(eventIds);
          setEventsAnchorEl(arg.jsEvent.target as HTMLElement);
      
          return 'none';
        }}
      />

      {open ? (
        <EventsFormDialog open={open} onClose={toggle} selectInfo={selectInfo} />
      ) : null}

      {selectedEventId ? (
        <EventPopover
          anchorEl={anchorEl}
          onClose={onClosePopover}
          eventId={selectedEventId}
        />
      ) : null}

      {!!eventsAnchorEl ? (
        <DayEventsPopover
          anchorEl={eventsAnchorEl}
          onClose={onCloseEventsPopover}
          eventsIds={eventsIdsForDate}
          selectedDate={selectedDate}
          openDayView={openDayView}
        />
      ) : null}
    </Fragment>
  );
}
 
export default CalendarBody;
